import React, { useCallback, useState } from 'react';

import { BuilderBlocks } from '@builder.io/react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { buildURL } from '~/techstyle-shared/redux-core';

const TabLabel = styled.button.attrs({ variant: 'unstyled' })`
  background: transparent;
  padding: 12px 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  height: 100%;
  color: ${({ theme }) => theme.colors.palette.shadow80};
  font-size: 16px;
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
  margin: 16px 0;
  cursor: pointer;

  /* Apply brand-specific tab label styles from theme */
  ${({ theme: { tabsNode } }) => tabsNode.sizeGuideTabLabel};

  &.selected {
    ${({ theme: { tabsNode } }) => tabsNode.sizeGuideTabLabelSelected};
    background: ${({ theme }) => theme.colors.palette.black};
    color: ${({ theme }) => theme.colors.palette.white};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  justify-content: center;
  width: fit-content;
  margin: 12px auto;

  ${({ theme: { tabsNode } }) => tabsNode.carousel};
`;

const TabWrapper = styled.div`
  justify-content: center;
`;

export const Context = React.createContext();

const BuilderSizeGuideTabs = ({ builderBlock, tabs }) => {
  const router = useRouter();

  const [activeTab, setActiveTab] = useState(0);

  const handleTabSelect = useCallback(
    (tabIndex) => {
      const tab = tabs[tabIndex];

      setActiveTab(tabIndex);

      if (tab?.tabId) {
        const pathname = router.pathname;
        const query = {
          ...router.query,
          tab: tab.tabId,
        };
        const asPath = buildURL(window.location.pathname, query);

        router.replace(
          {
            pathname,
            query,
          },
          asPath,
          {
            shallow: true,
          }
        );
      }
    },
    [router, tabs]
  );

  if (!tabs?.length) {
    return null;
  }

  return (
    <TabWrapper data-builder-component-type="size-guide-tabs">
      <Context.Provider value={{ isBuilderSizeGuideTabs: true }}>
        <TabsContainer className="builder-size-guide-tab-carousel-wrapper">
          {tabs?.map((tab, index) => (
            <TabLabel
              key={`${tab.tabId}-${index}`}
              onClick={() => {
                handleTabSelect(index);
              }}
              className={
                activeTab === index
                  ? 'builder-size-guide-tab-label selected'
                  : 'builder-size-guide-tab-label'
              }
            >
              <BuilderBlocks
                parentElementId={builderBlock.id}
                dataPath={`component.options.tabs.${index}.label`}
                blocks={tab.label}
                className={
                  activeTab === index
                    ? 'builder-size-guide-tab-tile selected'
                    : 'builder-size-guide-tab-tile'
                }
              />
            </TabLabel>
          ))}
        </TabsContainer>

        {tabs?.[activeTab] && (
          <div key={tabs[activeTab].tabId}>
            <BuilderBlocks
              key={tabs[activeTab].tabId}
              parentElementId={builderBlock.id}
              dataPath={`component.options.tabs.${activeTab}.content`}
              blocks={tabs[activeTab].content}
            />
          </div>
        )}
      </Context.Provider>
    </TabWrapper>
  );
};

BuilderSizeGuideTabs.propTypes = {
  /**
   * The builderBlock object this is rendering.
   */
  builderBlock: PropTypes.shape({
    id: PropTypes.string,
  }),

  /**
   * The size guide tab objects to display. Each tab contains a label and content with a preloaded Asset element.
   */
  tabs: PropTypes.array,
};

export default BuilderSizeGuideTabs;
